import { useEffectWithController } from '@carvana/merch-react-hooks';
import { useContext, useEffect, useMemo, useRef } from 'react';
import DeviceContext, { useIsMobile } from '~/context/common/DeviceContext';
import StaticContext from '~/context/common/StaticContext';
import { useUserLocation } from '~/context/common/UserContext';
import AvailabilityAndPriceContext from '~/context/home/AvailabilityAndPriceContext';
import MegaEventContext from '~/context/home/MegaEventContext';
import RecentVehiclesContext from '~/context/home/RecentVehiclesContext';
import fetchInventory from '~/services/common/fetchInventory';
import { BrowserCookies } from '~/utilities';
import {
  buildRecentInventoryRequest,
  sortByRecentOrder
} from '~/utilities/home/requests/recentInventory';
import toAvailabilityPayloads from '~/utilities/home/requests/toAvailabilityPayloads';
import { generateErrorLogger } from '~/utilities/loggers';

export default function useConvertRecentToSearch() {
  const {
    recentVehiclesFromRcb: { isLoaded: rcbIsLoaded, data: rcbVehicles },
    recentVehiclesFromSearch: { setLoading, setError, setData }
  } = useContext(RecentVehiclesContext);
  const { setRecentVehicles: setMegaEventRecentVehicles } =
    useContext(MegaEventContext);
  const {
    recentVehicleAvailability: {
      setData: setAvailabilityData,
      setError: setAvailabilityError
    }
  } = useContext(AvailabilityAndPriceContext);

  const rcbVehicleIds = useMemo(
    () => rcbVehicles.map(({ vehicleId }) => vehicleId),
    [rcbVehicles]
  );

  const { zip } = useUserLocation();
  const { clientId } = useContext(StaticContext);
  const { browserName } = useContext(DeviceContext);
  const isMobile = useIsMobile();
  const isMobileRef = useRef(isMobile);

  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  useEffectWithController(
    controller => {
      setLoading();

      if (!rcbIsLoaded) {
        return;
      }

      if (rcbVehicleIds.length === 0) {
        setData([]);
        setAvailabilityData([]);

        return;
      }

      fetchInventory(
        buildRecentInventoryRequest({
          analyticsData: {
            browser: browserName ?? '',
            clientId,
            deviceName: '',
            isMobileDevice: isMobileRef.current,
            referrer: document.referrer
          },
          browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
          vehicleIds: rcbVehicleIds,
          zip
        }),
        controller
      )
        .then(({ data: inventoryData }) => {
          const orderedVehicles = sortByRecentOrder(
            inventoryData.inventory.vehicles,
            rcbVehicleIds
          );
          setData(orderedVehicles);
          setMegaEventRecentVehicles(orderedVehicles);
          setAvailabilityData(toAvailabilityPayloads(orderedVehicles));
        })
        .catch(
          generateErrorLogger(
            'fetchSearchWithIds',
            {},
            {
              callback: err => {
                setError(err);
                setAvailabilityError(err);
              },
              controller
            }
          )
        );
    },
    [
      browserName,
      clientId,
      rcbIsLoaded,
      rcbVehicleIds,
      setAvailabilityData,
      setAvailabilityError,
      setData,
      setError,
      setLoading,
      setMegaEventRecentVehicles,
      zip
    ]
  );
}
